import { SVGProps } from 'react'

import Link from 'next/link'

import Box from 'components/atomic/atoms/box'
import Button from 'components/atomic/atoms/button'
import { IconStyled, RadixIcon } from 'components/atomic/atoms/icon'
import Text from 'components/atomic/atoms/text'

export type TemplateStartBuildingProps = {
  title?: string
  framework: string
  columns: {
    icon: RadixIcon | ((props: SVGProps<SVGSVGElement>) => JSX.Element)
    title: string
    text: string
    href: string
    buttonLabel: string
    external?: boolean
  }[]
}

const TemplateStartBuilding = ({
  title,
  framework,
  columns,
}: TemplateStartBuildingProps) => {
  return (
    <Box relative css={{ pb: '$12', spaceY: 10 }}>
      <Box textCenter css={{ spaceY: 4 }}>
        <Text
          as="h2"
          size={{ '@initial': '3xl', '@sm': '4xl' }}
          weight="semiBold"
        >
          {title ? title : `Start building with ${framework}`}
        </Text>
        <Text as="p" color="text2" size={{ '@initial': 'md', '@md': 'lg' }}>
          We’ve made it super easy to get started integrating Grafbase with{' '}
          {framework}.
        </Text>
      </Box>
      <Box
        grid
        items="start"
        css={{ gridCols: 1, gap: '$12', '@md': { gridCols: 3 } }}
      >
        {columns.map(
          ({ title, icon, text, external, buttonLabel, href }, index) => (
            <Box
              key={index}
              flex="column"
              items="center"
              justify="center"
              css={{ spaceY: 3, height: '$full' }}
            >
              <Box
                flex
                items="center"
                justify="center"
                css={{
                  borderRadius: '$round',
                  bgc: '$bg3',
                  width: 48,
                  height: 48,
                }}
              >
                <IconStyled as={icon} size="2xl" />
              </Box>
              <Text as="h3" size="2xl" weight="semiBold" align="center">
                {title}
              </Text>
              <Text
                as="p"
                color="text2"
                weight="medium"
                align="center"
                css={{ flex: 1 }}
              >
                {text}
              </Text>
              {external ? (
                <Text as="a" href={href} target="_blank" rel="noreferrer">
                  <Button variant="secondary">{buttonLabel}</Button>
                </Text>
              ) : (
                <Box as={Link} href={href} css={{ pt: '$4' }}>
                  <Button variant="secondary">{buttonLabel}</Button>
                </Box>
              )}
            </Box>
          ),
        )}
      </Box>
    </Box>
  )
}

export default TemplateStartBuilding
